import * as React from 'react'
import {
    Box,
    Card,
    CardContent,
    Divider,
    Grid,
    Stack,
    Switch,
    TextField,
    Typography
} from '@mui/material'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import ShieldRoundedIcon from '@mui/icons-material/ShieldRounded'

const SETTINGS_KEY = 'fortressBotSettings'

const DEFAULT_GLOBALS = Object.freeze({
    attackCountThreshold: '1800',
    scanIntervalHours: '1',
    maximumDistance: '500',
    useFeathers: false
})

const KINGDOMS = Object.freeze([
    {
        key: 'fire',
        title: 'Fire',
        pluginKey: 'attackfortresses',
        helper: 'Enable fortress attacks in fire kingdom'
    },
    {
        key: 'ice',
        title: 'Ice',
        pluginKey: 'attackfortresses_glacier',
        helper: 'Enable fortress attacks in ice kingdom'
    },
    {
        key: 'sand',
        title: 'Sand',
        pluginKey: 'attackfortresses_sand',
        helper: 'Enable fortress attacks in sand kingdom'
    }
])

const cloneValue = value =>
    typeof structuredClone === 'function' ?
        structuredClone(value) :
        JSON.parse(JSON.stringify(value ?? {}))

const textValue = (value, fallback = '') =>
    [undefined, null].includes(value) ? fallback : String(value)

const boolValue = (value, fallback = false) => {
    if ([undefined, null, ''].includes(value))
        return fallback

    if (typeof value === 'string') {
        const normalized = value.trim().toLowerCase()
        if (['true', '1', 'yes', 'on'].includes(normalized))
            return true
        if (['false', '0', 'no', 'off'].includes(normalized))
            return false
        return fallback
    }

    return Boolean(value)
}

const normalizePluginState = pluginState => {
    const source =
        pluginState?.[SETTINGS_KEY] && typeof pluginState[SETTINGS_KEY] === 'object' ?
            pluginState[SETTINGS_KEY] :
            {}

    return {
        attackCountThreshold: textValue(source.attackCountThreshold, DEFAULT_GLOBALS.attackCountThreshold),
        scanIntervalHours: textValue(source.scanIntervalHours, DEFAULT_GLOBALS.scanIntervalHours),
        maximumDistance: textValue(source.maximumDistance, DEFAULT_GLOBALS.maximumDistance),
        useFeathers: boolValue(source.useFeathers ?? pluginState?.useLeather, DEFAULT_GLOBALS.useFeathers),
        enabled: boolValue(source.enabled ?? pluginState?.state)
    }
}

export const buildFortressPluginState = (userPlugins, nextConfig, runtimeStateOverrides = {}) => {
    const nextPlugins = cloneValue(userPlugins ?? {})

    KINGDOMS.forEach(section => {
        const kingdom = nextConfig.kingdoms[section.key]

        nextPlugins[section.pluginKey] ??= {}
        nextPlugins[section.pluginKey][SETTINGS_KEY] = {
            attackCountThreshold: textValue(nextConfig.globals.attackCountThreshold, DEFAULT_GLOBALS.attackCountThreshold),
            scanIntervalHours: textValue(nextConfig.globals.scanIntervalHours, DEFAULT_GLOBALS.scanIntervalHours),
            maximumDistance: textValue(nextConfig.globals.maximumDistance, DEFAULT_GLOBALS.maximumDistance),
            useFeathers: Boolean(nextConfig.globals.useFeathers),
            enabled: Boolean(kingdom.enabled)
        }
        nextPlugins[section.pluginKey].useLeather = Boolean(nextConfig.globals.useFeathers)

        if (Object.prototype.hasOwnProperty.call(runtimeStateOverrides, section.key))
            nextPlugins[section.pluginKey].state = Boolean(runtimeStateOverrides[section.key])
    })

    return nextPlugins
}

const PRIMARY_CARD_SX = {
    backgroundColor: 'rgba(18, 29, 48, 0.82)',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: '22px',
    boxShadow: '0 16px 34px rgba(0,0,0,0.18)'
}

const FIELD_INPUT_SX = {
    '& .MuiOutlinedInput-root': {
        minHeight: 44,
        borderRadius: '10px',
        backgroundColor: 'rgba(23, 34, 55, 0.72)'
    },
    '& .MuiInputBase-input': {
        fontSize: '1.01rem',
        fontWeight: 700,
        color: '#f5f7ff'
    }
}

const TOGGLE_SX = {
    width: 48,
    height: 29,
    p: 0,
    '& .MuiSwitch-switchBase': {
        p: '2.5px',
        '&.Mui-checked': {
            transform: 'translateX(18px)',
            color: '#ffffff'
        },
        '&.Mui-checked + .MuiSwitch-track': {
            backgroundColor: '#2f7cff',
            opacity: 1
        }
    },
    '& .MuiSwitch-thumb': {
        width: 24,
        height: 24,
        boxShadow: 'none'
    },
    '& .MuiSwitch-track': {
        opacity: 1,
        borderRadius: '999px',
        backgroundColor: 'rgba(111, 123, 143, 0.9)'
    }
}

function HelperText({ children }) {
    return (
        <Stack direction="row" spacing={0.6} alignItems="center" sx={{ mt: 0.7 }}>
            <InfoOutlinedIcon sx={{ fontSize: 13, color: '#95a5c4' }} />
            <Typography sx={{ fontSize: '0.89rem', color: '#95a5c4', lineHeight: 1.35 }}>
                {children}
            </Typography>
        </Stack>
    )
}

function SectionHeading({ children }) {
    return (
        <Stack direction="row" alignItems="center" spacing={1.3} sx={{ mb: 2.2 }}>
            <Typography sx={{ color: '#f5f7ff', fontSize: '1.05rem', fontWeight: 800 }}>
                {children}
            </Typography>
            <Divider sx={{ flex: 1, borderColor: 'rgba(129, 146, 178, 0.22)' }} />
        </Stack>
    )
}

function NumericField({ label, value, helper, onChange }) {
    return (
        <Box>
            <Typography sx={{ color: '#f5f7ff', fontSize: '1rem', fontWeight: 800, mb: 1 }}>
                {label}
            </Typography>
            <TextField
                fullWidth
                value={value}
                onChange={event => onChange(event.target.value)}
                sx={FIELD_INPUT_SX}
            />
            <HelperText>{helper}</HelperText>
        </Box>
    )
}

function KingdomToggle({ label, helper, checked, onChange }) {
    return (
        <Box sx={{ minHeight: 54 }}>
            <Stack direction="row" spacing={1.2} alignItems="center" sx={{ pt: 1.15 }}>
                <Switch
                    checked={checked}
                    onChange={event => onChange(event.target.checked)}
                    sx={TOGGLE_SX}
                />
                <Typography sx={{ color: '#f5f7ff', fontSize: '1rem', fontWeight: 800 }}>
                    {label}
                </Typography>
            </Stack>
            <HelperText>{helper}</HelperText>
        </Box>
    )
}

export default function FortressBotSettingsPanel({ userPlugins, onChange }) {
    const globalPluginKey = React.useMemo(
        () =>
            KINGDOMS.find(section => userPlugins?.[section.pluginKey]?.[SETTINGS_KEY])?.pluginKey ??
            KINGDOMS[0].pluginKey,
        [userPlugins]
    )

    const config = React.useMemo(() => {
        const globalSource = normalizePluginState(userPlugins?.[globalPluginKey])

        return {
            globals: {
                attackCountThreshold: globalSource.attackCountThreshold,
                scanIntervalHours: globalSource.scanIntervalHours,
                maximumDistance: globalSource.maximumDistance,
                useFeathers: globalSource.useFeathers
            },
            kingdoms: Object.fromEntries(
                KINGDOMS.map(section => [section.key, normalizePluginState(userPlugins?.[section.pluginKey])])
            )
        }
    }, [globalPluginKey, userPlugins])

    const commit = React.useCallback((nextConfig, runtimeStateOverrides) => {
        onChange(buildFortressPluginState(userPlugins, nextConfig, runtimeStateOverrides))
    }, [onChange, userPlugins])

    const updateGlobalField = (key, value) =>
        commit({
            ...config,
            globals: {
                ...config.globals,
                [key]: value
            }
        })

    const updateKingdomEnabled = (key, enabled) =>
        commit({
            ...config,
            kingdoms: {
                ...config.kingdoms,
                [key]: {
                    ...config.kingdoms[key],
                    enabled
                }
            }
        }, { [key]: enabled })

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.4 }}>
            <Card sx={PRIMARY_CARD_SX}>
                <CardContent sx={{ p: { xs: 2.2, md: 3.2 } }}>
                    <Stack direction="row" spacing={1.3} alignItems="center" sx={{ mb: 0.7 }}>
                        <ShieldRoundedIcon sx={{ color: '#f5f7ff', fontSize: 21 }} />
                        <Typography sx={{ color: '#f5f7ff', fontSize: '1.55rem', fontWeight: 800 }}>
                            Fortress Bot Settings
                        </Typography>
                    </Stack>

                    <Typography sx={{ color: '#9badcb', fontSize: '1rem', mb: 4 }}>
                        Configure fortress attack parameters and kingdom settings you can change the Attack Setups in the Attack Setups Tab
                    </Typography>

                    <SectionHeading>Attack Settings</SectionHeading>
                    <Grid container spacing={2.4} sx={{ mb: 4.2 }}>
                        <Grid item xs={12} md={6}>
                            <NumericField
                                label="Attack Count Threshold"
                                value={config.globals.attackCountThreshold}
                                onChange={value => updateGlobalField('attackCountThreshold', value)}
                                helper="Number of attacks before stopping (Daily Limit is 3500 after that it will get more expensive)"
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <KingdomToggle
                                label="Use Feathers"
                                helper="Use feathers for every fortress attack"
                                checked={Boolean(config.globals.useFeathers)}
                                onChange={value => updateGlobalField('useFeathers', value)}
                            />
                        </Grid>
                    </Grid>

                    <SectionHeading>Scan Settings</SectionHeading>
                    <Grid container spacing={2.4} sx={{ mb: 4.2 }}>
                        <Grid item xs={12} md={6}>
                            <NumericField
                                label="Scan Interval (Hours)"
                                value={config.globals.scanIntervalHours}
                                onChange={value => updateGlobalField('scanIntervalHours', value)}
                                helper="How often to scan for new fortress targets"
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <NumericField
                                label="Maximum Distance"
                                value={config.globals.maximumDistance}
                                onChange={value => updateGlobalField('maximumDistance', value)}
                                helper="Maximum distance to scan for fortress targets"
                            />
                        </Grid>
                    </Grid>

                    <SectionHeading>Kingdom Settings</SectionHeading>
                    <Grid container spacing={2.6}>
                        {KINGDOMS.map(section => (
                            <Grid key={section.key} item xs={12} md={6}>
                                <KingdomToggle
                                    label={`${section.title}: Enable Kingdom`}
                                    helper={section.helper}
                                    checked={Boolean(config.kingdoms[section.key]?.enabled)}
                                    onChange={enabled => updateKingdomEnabled(section.key, enabled)}
                                />
                            </Grid>
                        ))}
                    </Grid>
                </CardContent>
            </Card>
        </Box>
    )
}
